import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useNavigate, useParams} from 'react-router-dom';
import PostsListUI from '../view/PostsListUI';
import PostsStore from '../../store/PostsStore';
import {
  POST_CAPTION_MAX_LENGTH,
  PostListItem,
} from '../../../../data/models/Posts/Post';
import {Course} from '../../../../data/models/Course/Course';
import ConfirmationModal from '../../../../uiToolkit/ConfirmationModal';
import ModalFade, {
  useModalFade,
} from '../../../Microreels/List/components/ModalFade';
import EditMicroreelInfoModal from '../../../Microreels/List/components/EditMicroreelInfoModal';
import MicroreelLearnersModal from '../../../Microreels/List/components/MicroreelLearnersModal';
import {postsText} from '../../postsText';
import {postPosterText} from '../../postPosterText';
import MoveToProjectModal from '../../../Projects/components/MoveToProjectModal';

interface Props {
  postsStore?: PostsStore;
}

const PostsListContainer = inject('postsStore')(
  observer(({postsStore}: Props) => {
    const [posts, setPosts] = useState<PostListItem[]>([]);
    const [page, setPage] = useState(0);
    const [pagesCount, setPagesCount] = useState(1);
    const [search, setSearch] = useState('');
    const [postToEdit, setPostToEdit] = useState<PostListItem>();
    const [courseToEdit, setCourseToEdit] = useState<Course>();
    const [postToDelete, setPostToDelete] = useState<PostListItem>();
    const [learnersPost, setLearnersPost] = useState<PostListItem>();
    const [postToMove, setPostToMove] = useState<PostListItem>();
    const {organizationId} = useParams();
    const navigate = useNavigate();

    const editFade = useModalFade(!!postToEdit);
    const deleteFade = useModalFade(!!postToDelete);

    const fetchPosts = () => {
      if (!organizationId) return;
      setPage(0);
      postsStore!.getPosts(organizationId, 0, search).then(res => {
        setPosts(res.data);
        setPagesCount(res.pagesCount);
      });
    };

    useEffect(() => {
      fetchPosts();
    }, [search, organizationId]);

    useEffect(() => {
      if (!organizationId || !postToEdit) {
        setCourseToEdit(undefined);
        return;
      }
      postsStore!.getPostCourse(organizationId, postToEdit.id).then(course => {
        if (!course) {
          setPostToEdit(undefined);
          return;
        }
        setCourseToEdit(course);
      });
    }, [postToEdit, organizationId]);

    const loadNextPage = async () => {
      if (!organizationId || page + 1 >= pagesCount) return;
      const res = await postsStore!.getPosts(organizationId, page + 1, search);
      setPosts(current => [...current, ...res.data]);
      setPage(page + 1);
    };

    const onCreate = () => {
      navigate(`/manage/${organizationId}/home/create_post`);
    };

    const onLearnerProgress = (post: PostListItem) => {
      if (!post.publishedCourseId) return;
      navigate(
        `/manage/${organizationId}/analytics/course/${post.publishedCourseId}/learners`,
        {state: {backPath: `/manage/${organizationId}/posts`}},
      );
    };

    const onOpen = (post: PostListItem) => {
      navigate(`/manage/${organizationId}/posts/${post.id}`);
    };

    const onSaveInfo = async (
      name: string,
      description: string,
      cover?: File,
    ) => {
      if (!organizationId || !courseToEdit) return;
      const success = await postsStore!.editPost(
        organizationId,
        {...courseToEdit, title: name, description},
        postToEdit?.lessonId,
        cover,
      );
      if (success) {
        setPostToEdit(undefined);
        fetchPosts();
      }
    };

    const onConfirmDelete = async () => {
      if (!organizationId || !postToDelete) return;
      const success = await postsStore!.deletePost(
        organizationId,
        postToDelete.id,
      );
      setPostToDelete(undefined);
      if (success) {
        fetchPosts();
      }
    };

    return (
      <>
        <PostsListUI
          posts={posts}
          isLoading={postsStore!.isLoading}
          search={search}
          setSearch={setSearch}
          onCreate={onCreate}
          onOpen={onOpen}
          onEditInfo={setPostToEdit}
          onLearners={setLearnersPost}
          onLearnerProgress={onLearnerProgress}
          onDelete={setPostToDelete}
          onMoveToProject={setPostToMove}
          loadNextPage={loadNextPage}
        />
        {editFade.rendered && (
          <ModalFade closing={editFade.closing}>
            <EditMicroreelInfoModal
              shown
              headerTitle={postsText.editInfoTitle}
              initialName={courseToEdit?.title}
              initialDescription={courseToEdit?.description}
              isLoading={postsStore!.isEditLoading}
              onCancel={() => setPostToEdit(undefined)}
              namePlaceholder={postsText.postTitlePlaceholder}
              descriptionField={postsText.caption}
              descriptionPlaceholder={postsText.captionPlaceholder}
              maxDescriptionLength={POST_CAPTION_MAX_LENGTH}
              posterText={postPosterText}
              posterAspectRatio="9 / 16"
              posterMaxWidth={190}
              initialCover={postToEdit?.poster}
              onSave={onSaveInfo}
            />
          </ModalFade>
        )}
        {deleteFade.rendered && (
          <ModalFade closing={deleteFade.closing}>
            <ConfirmationModal
              shown
              title={postsText.deleteConfirm}
              acceptText={postsText.delete}
              onAccept={onConfirmDelete}
              onCancel={() => setPostToDelete(undefined)}
            />
          </ModalFade>
        )}
        {postToMove && (
          <MoveToProjectModal
            courseId={postToMove.id}
            currentProjectId={postToMove.projectId}
            onClose={() => setPostToMove(undefined)}
            onMoved={() => {
              setPostToMove(undefined);
              fetchPosts();
            }}
          />
        )}
        {learnersPost && learnersPost.publishedCourseId && (
          <MicroreelLearnersModal
            groupTitle={learnersPost.title}
            publishedCourseId={learnersPost.publishedCourseId}
            onClose={() => setLearnersPost(undefined)}
          />
        )}
      </>
    );
  }),
);

export default PostsListContainer;
